// ============================================================================
// MessageThread — the shared 1:1 chat thread (Wave 5), reused by BOTH realms.
// ----------------------------------------------------------------------------
// One component, two callers:
//   • the CLIENT portal thread  (/me/messages)         — realm 'client'
//   • the TRAINER client-detail Messages tab           — realm 'trainer'
// The caller owns its own React Query hooks (client vs trainer API + keys) and
// passes the thread data + send/attach/mark-read handlers + the attachment-URL
// resolver down here. This component owns the PRESENTATION only:
//   • sender-aligned message bubbles ("mine" right/coral, the other party left)
//   • day separators + per-message timestamps (localized, EN + 繁中)
//   • body rendered as SAFE RICH TEXT via renderRichText() — the plain-text body
//     is TOKENIZED into React nodes (auto-linked http/https/mailto URLs, **bold**,
//     _italic_, newlines->breaks). React escapes every text run; NEVER
//     dangerouslySetInnerHTML, no HTML/markdown injection. Emoji are unicode.
//   • a subtle "Seen" read receipt on my latest read message
//   • a composer: text input + the shared unicode EmojiPicker + an image attach
//     button (≤2 MB validated client-side; the image is fetched back via the
//     owner-gated decrypt-serve URL the caller supplies)
// The caller is responsible for marking the thread read on open (it knows the
// unread count from its own query). Mobile-first, Pulse, reduced-motion aware.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "framer-motion";
import { EmptyState } from "./EmptyState";
import { EmojiPicker } from "./EmojiPicker";
import { ApiError } from "../api";
import type { Message } from "../api";
import { cn } from "../lib/cn";
import { renderRichText } from "../lib/richText";
import { intlLocale } from "../i18n/config";

/** The 2 MB ceiling — matches the server cap on raw bytes (spec §6/§8). */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/** Which sender's bubbles align right (coral) — the viewer's own messages. */
export type ThreadRealm = "client" | "trainer";

export interface MessageThreadHandlers {
  /** Send a plain-text message. Resolves when persisted. */
  send: (body: string) => Promise<unknown>;
  /** True while a text send is in flight (disables the send button). */
  sending: boolean;
  /** Send an image attachment (+ optional caption). May throw ApiError. */
  sendAttachment: (file: File, body?: string) => Promise<unknown>;
  /** True while an attachment upload is in flight. */
  attaching: boolean;
  /** Owner-gated decrypt-serve URL for a thread image, by its photo id. */
  attachmentUrl: (photoId: number) => string;
}

export interface MessageThreadProps {
  /** Whose messages are "mine" (right-aligned, coral). */
  realm: ThreadRealm;
  messages: Message[];
  loading: boolean;
  errored: boolean;
  handlers: MessageThreadHandlers;
  /** Empty-state copy override (the trainer side reads a different key). */
  emptyLabel?: string;
  /** Optional fixed height utility (defaults to a tall mobile thread). */
  className?: string;
}

/** A localized day label ("Today" / "Yesterday" / a short date) for separators. */
function dayLabel(iso: string, t: (k: string) => string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return t("today");
  if (sameDay(d, yest)) return t("yesterday");
  return d.toLocaleDateString(intlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** A localized HH:MM time for a single message. */
function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(intlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A YYYY-MM-DD key for grouping messages into day separators. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function MessageThread({
  realm,
  messages,
  loading,
  errored,
  handlers,
  emptyLabel,
  className,
}: MessageThreadProps) {
  const { t } = useTranslation("messages");
  const reduce = useReducedMotion();

  const [draft, setDraft] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Keep the latest message in view as the thread grows / a message is sent.
  const lastId = messages.length ? messages[messages.length - 1].id : 0;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "end",
    });
  }, [lastId, reduce]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || handlers.sending) return;
    void handlers.send(body).then(() => {
      setDraft("");
      inputRef.current?.focus();
    });
  };

  const onPickEmoji = (emoji: string) => {
    setDraft((d) => d + emoji);
    setShowEmoji(false);
    inputRef.current?.focus();
  };

  const onChooseFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttachError(null);
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setAttachError(t("attachment.invalid"));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setAttachError(t("attachment.tooLarge"));
      return;
    }
    const caption = draft.trim();
    handlers
      .sendAttachment(file, caption || undefined)
      .then(() => setDraft(""))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 413) {
          // 413 covers both >2 MB (caught above) and over-quota.
          setAttachError(t("attachment.quota"));
        } else if (err instanceof ApiError && err.status === 422) {
          setAttachError(t("attachment.invalid"));
        } else {
          setAttachError(t("attachment.error"));
        }
      });
  };

  // Group messages by day for the separators.
  const groups = useMemo(() => {
    const out: Array<{ key: string; label: string; items: Message[] }> = [];
    for (const m of messages) {
      const k = dayKey(m.created_at);
      const last = out[out.length - 1];
      if (last && last.key === k) {
        last.items.push(m);
      } else {
        out.push({ key: k, label: dayLabel(m.created_at, t), items: [m] });
      }
    }
    return out;
  }, [messages, t]);

  // The id of MY last message the other party has READ — the bubble that carries
  // the "Seen" receipt (only on the most-recent read own message).
  const lastSeenOwnId = useMemo(() => {
    let id = 0;
    for (const m of messages) {
      if (m.sender === realm && m.read_at) id = m.id;
    }
    return id;
  }, [messages, realm]);

  return (
    <div className={cn("flex flex-col", className ?? "h-[72vh]")}>
      {/* Thread */}
      <div
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto rounded-xl bg-surface-sunken/40 p-3 ring-1 ring-line"
      >
        {loading && <ThreadSkeleton />}

        {errored && (
          <p className="py-6 text-center text-sm text-body-soft">
            {t("loadError")}
          </p>
        )}

        {!loading && !errored && messages.length === 0 && (
          <div className="grid h-full place-items-center">
            <EmptyState title={emptyLabel ?? t("empty")} />
          </div>
        )}

        {groups.map((g) => (
          <div key={g.key} className="space-y-2">
            <div className="flex justify-center">
              <span className="rounded-pill bg-surface px-3 py-1 text-[0.7rem] font-bold text-body-faint ring-1 ring-line">
                {g.label}
              </span>
            </div>
            {g.items.map((m) => (
              <Bubble
                key={m.id}
                message={m}
                realm={realm}
                seen={m.id === lastSeenOwnId}
                attachmentUrl={handlers.attachmentUrl}
              />
            ))}
          </div>
        ))}

        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="relative mt-3 shrink-0">
        {attachError && (
          <p className="mb-2 rounded-md bg-coral-soft px-3 py-2 text-sm font-bold text-coral-600 ring-1 ring-inset ring-coral/15">
            {attachError}
          </p>
        )}

        {showEmoji && (
          <EmojiPicker onPick={onPickEmoji} onClose={() => setShowEmoji(false)} />
        )}

        <div className="flex items-end gap-1.5 rounded-2xl border border-line bg-surface p-1.5">
          {/* Emoji */}
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            aria-label={t("emoji.open")}
            aria-expanded={showEmoji}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-body-soft transition-colors hover:bg-surface-sunken hover:text-body"
          >
            <EmojiGlyph />
          </button>

          {/* Attach image */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={handlers.attaching}
            aria-label={t("attachment.add")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-body-soft transition-colors hover:bg-surface-sunken hover:text-body disabled:opacity-50"
          >
            {handlers.attaching ? <Spinner /> : <PaperclipGlyph />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onChooseFile}
          />

          {/* Text */}
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("placeholder")}
            aria-label={t("placeholder")}
            enterKeyHint="send"
            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-base text-body outline-none placeholder:text-body-faint"
          />

          {/* Send */}
          <button
            type="submit"
            disabled={!draft.trim() || handlers.sending}
            aria-label={t("send")}
            className={cn(
              "grid h-9 w-9 shrink-0 place-items-center rounded-full transition-all",
              draft.trim() && !handlers.sending
                ? "bg-grad-coral text-white shadow-volt active:scale-90"
                : "bg-surface-sunken text-body-faint",
            )}
          >
            {handlers.sending ? <Spinner /> : <SendGlyph />}
          </button>
        </div>
      </form>
    </div>
  );
}

function Bubble({
  message,
  realm,
  seen,
  attachmentUrl,
}: {
  message: Message;
  realm: ThreadRealm;
  seen: boolean;
  attachmentUrl: (photoId: number) => string;
}) {
  const { t } = useTranslation("messages");
  const mine = message.sender === realm;
  const hasImage = message.photo_id != null;
  return (
    <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[78%]", mine ? "items-end" : "items-start")}>
        <div
          className={cn(
            "overflow-hidden rounded-2xl text-[0.95rem] leading-relaxed shadow-xs",
            mine
              ? "rounded-br-md bg-grad-coral text-white"
              : "rounded-bl-md bg-surface text-body ring-1 ring-line",
          )}
        >
          {hasImage && (
            <a
              href={attachmentUrl(message.photo_id!)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("attachment.open")}
              className="block"
            >
              <img
                src={attachmentUrl(message.photo_id!)}
                alt={t("attachment.alt")}
                loading="lazy"
                className="max-h-64 w-full object-cover"
              />
            </a>
          )}
          {/* SAFE RICH TEXT — tokenized into React nodes (links/bold/italic +
              line breaks); React escapes every text run. Never an HTML string,
              never dangerouslySetInnerHTML, so hostile markup renders as text. */}
          {message.body && (
            <p className="whitespace-pre-wrap break-words px-3.5 py-2">
              {renderRichText(message.body, { onCoral: mine })}
            </p>
          )}
        </div>
        <div
          className={cn(
            "mt-1 flex items-center gap-1.5 px-1 text-[0.68rem] text-body-faint",
            mine ? "justify-end" : "justify-start",
          )}
        >
          <span>{timeLabel(message.created_at)}</span>
          {mine && seen && (
            <span className="font-bold text-coral-600">{t("read")}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="space-y-3" aria-hidden>
      {[
        { mine: false, w: "w-40" },
        { mine: true, w: "w-32" },
        { mine: false, w: "w-52" },
        { mine: true, w: "w-44" },
      ].map((s, i) => (
        <div key={i} className={cn("flex", s.mine ? "justify-end" : "justify-start")}>
          <div className={cn("h-10 animate-pulse rounded-2xl bg-surface-sunken", s.w)} />
        </div>
      ))}
    </div>
  );
}

// --- inline glyphs (no icon dependency) -------------------------------------

function EmojiGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M9 10h.01M15 10h.01M8.5 14a4 4 0 0 0 7 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function PaperclipGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8-8a3.3 3.3 0 0 1 4.7 4.7l-8 8a1.6 1.6 0 0 1-2.3-2.3l7.3-7.3"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function SendGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path d="M4 12 20 4l-5 16-3.5-6.5L4 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 animate-spin" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
